import type { SimpleVFrameBase } from '@festina/legalentity-general-types/legalentity'
import type { VFrameBase } from '@festina/life-general-types/life'
import { useAuthenticationContext } from '@festina-life/authentication'
import {
  type Except,
  isEmpty,
  type SetRequired,
  type SourceReaderFunction,
  translationUtils,
} from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import {
  type ComponentProps,
  type ReactElement,
  useEffect,
  useMemo,
} from 'react'

import { useOptionalLifeFieldSetContext } from '../../context/lifeFieldSetContext'
import type { translations } from '../../translations/translations'
import { useLifeFormikContext } from '../forms/useLifeFormikContext'
import {
  isASimpleVFrame,
  isDontUseVFrame,
  isEnumeratedVFrame,
} from '../forms/yup/validation/types'
// oxlint-disable no-restricted-imports
import { LifeDateInput } from './lifeInput/LifeDateInput'
import { LifeDoubleInput } from './lifeInput/LifeDoubleInput'
import { LifeEnumeratedInput } from './lifeInput/LifeEnumeratedInput'
import { LifeMultiSelect } from './lifeInput/LifeMultiSelect'
import { LifeNumberInput } from './lifeInput/LifeNumberInput'
import { LifePercentageInput } from './lifeInput/LifePercentageInput'
import { LifeSelect } from './lifeInput/LifeSelect'
import { LifeTextInput } from './lifeInput/LifeTextInput'
import {
  fallbackInputTypeToVFrame,
  type InputType,
  type LifeInputWrapperType,
  simpleVFrameToInputType,
} from './lifeInputUtility'
import { useFieldVFrame } from './useFieldVFrame'

const vFrameToLifeInput = <V,>({
  frame,
  isInTestMode,
  props,
  translate,
  isFormDisabled,
}: {
  frame: SimpleVFrameBase
  isInTestMode: boolean
  props: Except<LifeInputProps<V>, 'fallbackInputType'>
  translate: SourceReaderFunction<(typeof translations)['en-GB']>
  isFormDisabled: boolean
}): ReactElement | null => {
  const inputType = simpleVFrameToInputType[frame._type]?.inputType

  // Strings naturally doesn't have a formatter.
  // As it would be impossible to write something, and immediately loose it.
  // The formatter is used on EnumeratedStrings though, and when we don't have a frame
  // We don't know that it was enumerated initially and that we used a formatter.
  // Therefor we check that the form is disabled, and the input type is string.
  const inputTypeOverwrite =
    isFormDisabled && inputType === 'string' ? 'enumerated' : inputType

  if (isEmpty(inputTypeOverwrite)) {
    if (isInTestMode) {
      throw new Error(
        `LifeInput: No input component found for frame type ${frame._type}`,
      )
    } else {
      return null
    }
  }

  switch (inputTypeOverwrite) {
    case 'date': {
      return (
        <LifeDateInput
          {...(props as ComponentProps<typeof LifeDateInput>)}
          data-testid={`life-input-${inputTypeOverwrite}`}
        />
      )
    }
    case 'boolean': {
      return (
        <LifeSelect<boolean>
          renderFn={(x) => translationUtils.translateBoolean(x, translate)}
          {...(props as ComponentProps<typeof LifeSelect<boolean>>)}
          data-testid={`life-input-${inputTypeOverwrite}`}
          options={[true, false]}
        />
      )
    }
    case 'double': {
      return (
        <LifeDoubleInput
          {...(props as ComponentProps<typeof LifeDoubleInput>)}
          data-testid={`life-input-${inputTypeOverwrite}`}
        />
      )
    }
    case 'string': {
      return (
        <LifeTextInput
          {...(props as ComponentProps<typeof LifeTextInput>)}
          data-testid={`life-input-${inputTypeOverwrite}`}
        />
      )
    }
    case 'integer': {
      return (
        <LifeNumberInput
          {...(props as ComponentProps<typeof LifeNumberInput>)}
          data-testid={`life-input-${inputTypeOverwrite}`}
        />
      )
    }
    case 'percentage': {
      return (
        <LifePercentageInput
          {...(props as ComponentProps<typeof LifePercentageInput>)}
          data-testid={`life-input-${inputTypeOverwrite}`}
        />
      )
    }
    case 'enumerated': {
      const typedProps = props as ComponentProps<typeof LifeSelect>

      return (
        <LifeEnumeratedInput
          {...{
            ...typedProps,
            shouldShowClearButton: isFormDisabled
              ? false
              : typedProps.shouldShowClearButton,
          }}
          data-testid={`life-input-${inputTypeOverwrite}`}
        />
      )
    }
    default:
      return null
  }
}

type RequireLabelUnlessTableCell<T> = T extends { isTableCell: true }
  ? T
  : SetRequired<T, Extract<'label', keyof T>>

type MultiSelectComponentType<V> = Except<
  ComponentProps<typeof LifeMultiSelect<V>>,
  'isValueCreator' | 'valueFromString'
> & {
  fallbackInputType: 'array'
}

export type LifeInputProps<V> = RequireLabelUnlessTableCell<
  | LifeInputWrapperType<typeof LifeDateInput, 'date', string>
  | LifeInputWrapperType<typeof LifeSelect<boolean>, 'boolean', boolean>
  | LifeInputWrapperType<typeof LifeDoubleInput, 'double', number>
  | LifeInputWrapperType<typeof LifeTextInput, 'string', string>
  | LifeInputWrapperType<typeof LifeNumberInput, 'integer', number>
  | LifeInputWrapperType<typeof LifePercentageInput, 'percentage', number>
  | MultiSelectComponentType<V>
>

/**
 *  General input used in life forms to dynamically render the correct input based on the VFrame.
 */
export const LifeInput = <V,>({
  fallbackInputType,
  ...props
}: LifeInputProps<V>) => {
  const translate = useTranslate()
  const { isInTestMode } = useAuthenticationContext()
  const { status, setFieldValue } = useLifeFormikContext()
  const frameStatus = useFieldVFrame(props.name)
  const shouldFieldsBeDisabled = status.shouldFieldsBeDisabled

  const { onChangeField, isMounted } = useOptionalLifeFieldSetContext()

  useEffect(() => {
    if (isMounted) {
      onChangeField?.({
        name: props.name,
        isDontUse: isDontUseVFrame(frameStatus),
      })
    }
  }, [props.name, frameStatus, isMounted, onChangeField])

  const frame = useMemo(() => {
    if (
      props.options?.length &&
      !isEnumeratedVFrame(frameStatus) &&
      !isDontUseVFrame(frameStatus)
    ) {
      const object: Record<InputType, VFrameBase | undefined> = {
        boolean: { ...frameStatus, _type: 'EnumeratedBooleanVFrame' },
        date: { ...frameStatus, _type: 'EnumeratedDateVFrame' },
        double: { ...frameStatus, _type: 'EnumeratedDecimalVFrame' },
        integer: { ...frameStatus, _type: 'EnumeratedIntegerVFrame' },
        percentage: { ...frameStatus, _type: 'EnumeratedFractionVFrame' },
        string: { ...frameStatus, _type: 'EnumeratedStringVFrame' },
        array: { ...frameStatus, _type: 'EnumeratedStringVFrame' },
        enumerated: undefined,
      }

      // As the concept frame is created solely to support configuration and validation. And is therefor not created for the purpose of the portal.
      // Some things must be handled by the frontend. One day we'll perhaps be able to create rules how frames "should" be created and work, so a lot of boilerplate could be removed.
      // So. In a perfect world we wouldn't need things like this. But issues such as:
      // The backend having an enum value that shows values that only an specific system should use
      // And the places we use options where we have a frame shouldn't be there.
      // We don't want to negatively impact the endusers.
      const backupFrame = object[fallbackInputType]
      if (backupFrame) {
        return backupFrame
      }
    }
    return frameStatus ?? fallbackInputTypeToVFrame[fallbackInputType]
  }, [props.options, frameStatus, fallbackInputType])

  useEffect(() => {
    if (isDontUseVFrame(frameStatus)) {
      void setFieldValue(props.name, undefined)
    }
  }, [frameStatus, props.name, setFieldValue])

  if (isDontUseVFrame(frameStatus)) {
    return null
  }

  if (
    (isASimpleVFrame(frame) && frame.isFramingListValue) ||
    fallbackInputType === 'array'
  ) {
    const hasEnumeratedValues = isEnumeratedVFrame(frame) && frame?.values

    const explicitProps = props as MultiSelectComponentType<V>
    if (hasEnumeratedValues) {
      return <LifeMultiSelect {...explicitProps} />
    } else {
      return (
        <LifeMultiSelect
          {...explicitProps}
          isValueCreator
          valueFromString={(x) => x as V}
        />
      )
    }
  }

  return vFrameToLifeInput<V>({
    frame,
    isInTestMode,
    props,
    translate,
    isFormDisabled: shouldFieldsBeDisabled,
  })
}
